import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { authTokens, setAuthFailureHandler, isRemoteApiEnabled } from '@/api/client'
import { STORAGE_KEYS } from '@/constants'
import { isStaffRole } from '@/components/admin/access'
import { loginUser, logoutUser, getCurrentUser } from '@/services/authService'

const AuthContext = createContext(null)

function readStoredUser() {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.authUser)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

function persistUser(user) {
  if (user) {
    localStorage.setItem(STORAGE_KEYS.authUser, JSON.stringify(user))
  } else {
    localStorage.removeItem(STORAGE_KEYS.authUser)
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => readStoredUser())
  const [isHydrating, setIsHydrating] = useState(() => isRemoteApiEnabled() && Boolean(authTokens.access))

  const clear = useCallback(() => {
    setUser(null)
    persistUser(null)
    authTokens.clear()
  }, [])

  useEffect(() => {
    setAuthFailureHandler(() => clear())
    return () => setAuthFailureHandler(null)
  }, [clear])

  useEffect(() => {
    if (!isRemoteApiEnabled() || !authTokens.access) {
      return
    }
    let cancelled = false
    getCurrentUser()
      .then((fresh) => {
        if (cancelled) return
        setUser(fresh)
        persistUser(fresh)
      })
      .catch(() => {
        if (!cancelled) clear()
      })
      .finally(() => {
        if (!cancelled) setIsHydrating(false)
      })
    return () => {
      cancelled = true
    }
  }, [clear])

  const login = useCallback(async ({ email, password }) => {
    const payload = await loginUser({ email, password })
    authTokens.setTokens(payload.tokens.accessToken, payload.tokens.refreshToken)
    setUser(payload.user)
    persistUser(payload.user)
    return payload.user
  }, [])

  const logout = useCallback(async () => {
    if (authTokens.access) {
      try {
        await logoutUser()
      } catch {
        // best-effort; clear locally regardless
      }
    }
    clear()
  }, [clear])

  const value = useMemo(
    () => ({
      user,
      isAuthenticated: Boolean(user),
      isAdmin: user?.role === 'ADMIN',
      // Staff = anyone with an admin login (ADMIN, MANAGER, EMPLOYEE). This is
      // the first gate into /admin; which screens a role then gets is
      // `canAccessAdminRoute` in access.js, the same module this comes from.
      isStaff: isStaffRole(user?.role),
      role: user?.role ?? null,
      isHydrating,
      login,
      logout,
    }),
    [user, isHydrating, login, logout]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>')
  return ctx
}
